import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Functions,
  getFunctions,
  HttpsCallable,
  httpsCallable,
  httpsCallableData,
  HttpsCallableOptions,
  FunctionsErrorCode,
} from '@angular/fire/functions';
import { httpsCallableFromURL } from 'firebase/functions';
import { environment } from 'src/environments/environment';
import { getApp } from '@angular/fire/app';
import { AuthService } from '../auth/auth.service';
import { Claims } from '../user/claims.model';
import { MinimaxSpeechEmotions } from '../voice/voice.model';
import { Mic } from 'lucide-angular';

export interface SuggestionsResponse {
  suggestions: string[];
  promptTokenCount: number;
  candidatesTokenCount: number;
  inferenceDelay: number;
}

export interface UserListItem {
  uid: string;
  email: string;
  displayName: string;
  customClaims: Claims;
  disabled: boolean;
}

export enum CacheType {
  default = 'default',
  hit = 'hit',
  miss = 'miss',
  renew = 'renew',
  bypass = 'bypass',
}

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  connectionStatus: Boolean;
  //private expressiaFunctions: Functions = getFunctions(getApp('expressia'));

  private ttsCallable: HttpsCallable<
    {
      text: string;
      engine: string;
      voiceURI: string;
      cache: CacheType;
      emotion?: MinimaxSpeechEmotions;
      pitch?: number;
      speed?: number;
      volume?: number;
      language?: string;
    },
    { audio: any; cache?: CacheType; cachePath?: string; consumption?: number }
  >;

  private oauth2ConsentUrlCallable: HttpsCallable<
    {
      idToken: string;
      redirect?: string;
      scope?: Array<string>;
      approvalPrompt?: string;
    },
    { authUrl: string }
  >;
  private oauth2RedeemCodeCallable: HttpsCallable<
    { idToken: string; code: string; redirect?: string },
    { accessToken: string; expiryDate: number }
  >;
  private oauth2RefreshTokenCallable: HttpsCallable<
    any,
    { accessToken: string; expiryDate: number }
  >;
  // private customTokenCallable: HttpsCallable<any, { token: string }>;
  private suggestionV2Callable: HttpsCallable<
    { typedText: string; langCode: string },
    SuggestionsResponse
  >;

  private clearOauth2Callable: HttpsCallable<any, any>;

  private getUsersCallable: HttpsCallable<any,
    Array<UserListItem>>;

  private setCustomClaimsCallable: HttpsCallable<
    { uid: string; customClaims: Claims },
    any>;

  private invalidateVoiceCacheCallable: HttpsCallable<
    { audioUri: string },
    { success: boolean }>;

  private createUserStorageConflictsCallable: HttpsCallable<
    { fileName: string[], operation: string, errorMsg: string },
    { operation: any }>;

  private networkReportCallable: HttpsCallable<
    { mobileDataConversiaByte: number, mobileDataByte: number, wifiDataConversiaByte: number, wifiDataByte: number, lastTime: number, currentTime: number },
    { response: any }>;

  private pingCallable: HttpsCallable<{ deviceTimestamp: number }, { serverTimestamp: number; deviceTimestamp: number }>;

  private connectionMonitorInterval: any;

  private get functionsBaseUrl(): string {
    // Use local emulator routing for onRequest functions when enabled
    if (environment.useEmulators) {
      const projectId = environment.firebaseConfig.projectId;
      const region = 'us-central1';
      return `http://localhost:5001/${projectId}/${region}`;
    }
    return `https://${environment.functionsDomain}`;
  }



  // private getExpressiaToken: HttpsCallable<{idToken: string}, { token: string }>;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private functions: Functions
  ) {
    this.ttsCallable = httpsCallable(this.functions, 'tts', {
      timeout: 10000,
    });
    this.suggestionV2Callable = httpsCallable<
      { typedText: string; langCode: string },
      SuggestionsResponse
    >(this.functions, 'suggest', { timeout: 10000 });

    this.oauth2ConsentUrlCallable = httpsCallable<
      { idToken: string; redirect?: string; scope?: Array<string> },
      { authUrl: string }
    >(this.functions, 'oauth2ConsentUrl', { timeout: 10000 });

    this.oauth2RedeemCodeCallable = httpsCallable<
      { idToken: string; code: string; redirect?: string },
      any
    >(this.functions, 'oauth2RedeemCode', { timeout: 10000 });

    this.oauth2RefreshTokenCallable = httpsCallable<
      any,
      { accessToken: string; expiryDate: number }
    >(this.functions, 'oauth2RefreshToken', { timeout: 10000 });

    this.ttsCallable = httpsCallable(this.functions, 'tts', {
      timeout: 10000,
    });

    this.clearOauth2Callable = httpsCallable(this.functions, 'oauth2RevokeToken', {
      timeout: 10000,
    });

    this.getUsersCallable = httpsCallable(this.functions, 'getUsers', {
      timeout: 10000,
    });

    this.setCustomClaimsCallable = httpsCallable(
      this.functions,
      'setCustomClaims',
      {
        timeout: 10000,
      }
    );

    this.invalidateVoiceCacheCallable = httpsCallable(
      this.functions,
      'invalidateVoiceCache',
      {
        timeout: 10000,
      }
    );

    this.createUserStorageConflictsCallable = httpsCallable(
      this.functions,
      'createUserStorageConflicts',
      {
        timeout: 10000,
      }
    );

    this.networkReportCallable = httpsCallable(this.functions, 'networkReport', {
      timeout: 10000,
    });

    this.pingCallable = httpsCallable(this.functions, 'ping', {
      timeout: 3000,
    });

    this.pingTest();

    // this.connectionMonitorInterval = setInterval( () => {
    //   this.pingTest();
    // }, 30000);
  }

  pingTest() {
    this.pingCallable({ deviceTimestamp: Date.now() })
      .then((result) => {
        // console.log('Ping successful: ', Date.now() - result.data.serverTimestamp, 'ms');
        this.connectionStatus = true;
      })
      .catch((error) => {
        console.log('Ping failed:', error);
        this.connectionStatus = false;
      });
  }

  dictionaryServerHttpGet(idiom): Observable<string> {
    //console.log("Requesting https://telepatixweb.tecladointeligente.com.br/database-server/" + idiom);
    return this.http.get(
      'https://telepatixweb.tecladointeligente.com.br/database-server/' +
      idiom +
      '/',
      { responseType: 'text' }
    );
  }

  tobiiEyeTrackerHttpEyePosition(): Observable<string> {
    return this.http.get('http://localhost:8080/test', {
      responseType: 'text',
    });
  }

  // Envia uma requisição POST
  serverPost(url: string, data: string): Observable<string> {
    return this.http.post(url, data, { responseType: 'text' });
  }

  getSuggestionsFromCloudAutoCompleteAi(
    typedText: string,
    langCode = 'pt-BR',
    uid: string,
    application: string,
  ): Observable<string> {
    // const res = this.suggestionV2Callable({sentence: typedText.toLowerCase(), langCode});
    // console.log('getSuggestionsFromCloudAutoCompleteAi', res);

    // const addMessage = httpsCallable(this.functions, 'suggest');
    // addMessage({ text: '1234' })
    //   .then((result) => {
    //     // Read result of the Cloud Function.
    //     /** @type {any} */
    //     const data = result.data;
    //     //const sanitizedMessage = data.text;
    //   });

    const url = `${this.functionsBaseUrl}/getSuggestions?text=${encodeURIComponent(
      typedText.toLowerCase()
    )}&langCode=${langCode}&uid=${uid}&application=${application}`;
    // Sem timeout, uma requisição pendurada deixa o quadrinho de sugestões girando
    // pra sempre (o catchError dos consumidores nunca dispara). 8s força o erro e o fallback.
    return this.http.get(url, { responseType: 'text' }).pipe(
      timeout({ each: 8000 })
    );
  }

  getSuggestionsFromCloud(typedText: string, langCode = 'pt-BR') {
    return this.suggestionV2Callable({
      typedText: typedText.toLowerCase(),
      langCode,
    });
  }

  getOauth2ConsentUrl(idToken: string, redirect: string = '') {
    return this.oauth2ConsentUrlCallable({ idToken, redirect });
  }

  getOauth2RefreshToken(idToken: string, code: string, redirect: string) {
    return this.oauth2RedeemCodeCallable({ idToken, code, redirect });
  }

  getOauth2AccessToken() {
    return this.oauth2RefreshTokenCallable();
  }

  clearOauth2() {
    return this.clearOauth2Callable();
  }

  getUsers() {
    return this.getUsersCallable();
  }

  setCustomClaims(uid: string, customClaims: Claims) {
    return this.setCustomClaimsCallable({ uid, customClaims });
  }

  getTts(
    sentence: string,
    engine: string,
    voiceURI: string,
    cache: CacheType = CacheType.default,
    emotion: MinimaxSpeechEmotions = MinimaxSpeechEmotions.AUTO,
    pitch?: number,
    speed?: number,
    volume?: number,
    language?: string
  ) {
    return this.ttsCallable({
      text: sentence,
      engine,
      voiceURI,
      cache,
      emotion,
      pitch,
      speed,
      volume,
      language,
    })
      .then((result) => result.data)
      .catch((error) => {
        console.error('Error in getTts', error);
        return Promise.reject(error);
      });
  }

  async getTtsStreamV2(
    sentence: string,
    engine: string,
    cache: CacheType = CacheType.default,
    voiceURI?: string,
    emotion?: MinimaxSpeechEmotions,
    application?: string,
    pitch?: number,
    rate?: number,
    volume?: number,

  ) {
    const url = `${this.functionsBaseUrl}/ttsStreamV3`;
    const headers = {
      'Authorization': 'Bearer ' + (await this.auth.getIdToken()),
      'Accept': 'application/json, audio/mpeg',
    };
    console.log('Headers:', headers);
    let body: any = {};
    
    if (engine === 'eleven') {
      body = {
        text: sentence,
        engine: engine,
        voiceURI: voiceURI,
        cache: cache,
        application,
      };
    } else if (engine === 'minimax') {
      body = {
        text: sentence,
        engine: engine,
        voiceURI: voiceURI,
        cache: cache,
        application,
        emotion: emotion,
        pitch: pitch,
        rate: rate,
        volume: volume,
      };
    }
    console.log('Request Stream of:', body);

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.body; // ReadableStream<Uint8Array>
  }

  async getTtsV2(
    sentence: string,
    engine: string,
    voiceURI: string,
    cache: CacheType = CacheType.default
  ) {
    //Call firebase onRequest usinh http passing auth token and bearer token
    //Return a stream of the audio
    console.log('getTtsStream', sentence, engine, voiceURI, cache);
    const url = `${this.functionsBaseUrl}/ttsStreamV2`;
    console.log(url);

    //Create header with the Bearer token
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + (await this.auth.getIdToken()),
      Accept: 'application/json, audio/mpeg',
    });
    console.log('[TTS]', JSON.stringify({ text: sentence, engine, voiceURI, cache }))

    return this.http
      .post(
        url,
        JSON.stringify({ text: sentence, engine, voiceURI, cache }),
        { headers, observe: 'response', responseType: 'blob' }
      ); // observable<HTTPResponse<Blob>>
  }

  // async elevenTts(sentence: string, voiceId: string) {
  //   //await this.voiceV1Callable({sentence, voiceId});
  //   return await this.elevenTtsDirect(sentence, voiceId);
  // }

  // async elevenTtsDirect(sentence: string, voiceId: string) {
  //   const apiKey = 'ca8433b8cffe9cd708e882a4f3b95c4b';
  //   const voiceID =
  //     'pNInz6obpgDQGcFmaJgB?optimize_streaming_latency=3&output_format=mp3_44100_96'; // The ID of the voice you want to get
  //   const textInput = 'sentence'; // The text you wish to convert to speech
  //   const elevenLabsAPI = 'https://api.elevenlabs.io/v1';
  //   const voiceURL = `${elevenLabsAPI}/text-to-speech/${voiceId}?optimize_streaming_latency=3&output_format=mp3_44100_96`;

  //   const data = {
  //     text: sentence,
  //     voice_settings: {
  //       stability: 0.3,
  //       similarity_boost: 1,
  //       style: 0,
  //       use_speaker_boost: false,
  //     },
  //     model_id: 'eleven_multilingual_v2',
  //   };

  //   const headers = {
  //     accept: 'audio/mpeg',
  //     'xi-api-key': apiKey,
  //     'Content-Type': 'application/json',
  //   };

  //   return this.http.post(voiceURL, data, {
  //     headers,
  //     observe: 'response',
  //     responseType: 'blob',
  //   });
  // }

  async invalidateVoiceCache(audioUri: string) {
    return (await this.invalidateVoiceCacheCallable({ audioUri })).data;
  }

  async createUserStorageConflicts(fileName: string[], operation: string, errorMsg: string) {
    return this.createUserStorageConflictsCallable({ fileName, operation, errorMsg });
  }

  networkReport(mobileDataConversiaByte: number, mobileDataByte: number,
    wifiDataConversiaByte: number, wifiDataByte: number,
    lastTime: number, currentTime: number) {
    return this.networkReportCallable({
      mobileDataConversiaByte,
      mobileDataByte,
      wifiDataConversiaByte,
      wifiDataByte,
      lastTime,
      currentTime,
    });
  }
}
